import { SocketState } from "@components/Context/SocketContext";
import Footer from "@components/Layout/Footer";
import Header from "@components/Layout/Header";
import http from "@services/http";
import { RootDispatch } from "@store";
import { logout } from "@store/auth/actions";
import { isTokenAlive } from "@store/auth/cookieStorage";
import { ROUTES_E } from "@utils/RoutsName";
import { SuspendComponent } from "@utils/SuspenseComponents";
import { getLangFromLocal, saveLangToLocal } from "@utils/lang";
import { ConfigProvider } from "antd";
import enUS from "antd/lib/locale/en_US";
import ruRU from "antd/lib/locale/ru_RU";
import { LazyMotion, domAnimation } from "framer-motion";
import moment from "moment";
import "moment/locale/ru";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Route, BrowserRouter, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { IS_DEBUG } from "./config";
import { LocaleContext, LocaleLangE } from "./i18n/LocaleContext";
import ProviderIntl, { GlobalIntl } from "./i18n/ProviderIntl";
import useCurrentUser, {
  useAllContractsUpdater,
  useAllMinerProfiles,
} from "./utils/hooks/useCurrentUser";
// import * as dayjs from "dayjs";
import AUTHSTATES from "@store/auth/AuthStates";
import { useAuth } from "@utils/hooks/useAuth";
import "dayjs/locale/en";
import "dayjs/locale/ru";
import { Menu } from "@components/Layout/Menu";
import styles from "./styles/App.module.scss";
import badge from "./styles/badge.module.scss";
import button from "./styles/button.module.scss";
import checkbox from "./styles/checkbox.module.scss";
import radio from "./styles/radio.module.scss";
import timePicker from "./styles/timePicker.module.scss";
import select from "./styles/select.module.scss";
import input from "./styles/input.module.scss";
import switchStyle from "./styles/switch.module.scss";
import tabs from "./styles/tabs.module.scss";
import transfer from "./styles/transfer.module.scss";
import classNames from "classnames";
import { ScrollableArea } from "@components/global/new/ScrollableArea/ScrollableArea";

export enum ComponentsE {
  HOME = "Home",
  LOGIN = "Login",
  USER = "User",
  MACHINES = "Machines",
  SPAREPARTS = "SpareParts",
  CLIENTS = "Clients",
  SETTINGS = "Settings",
  LOCATIONS = "Locations",
  CONTRACTS = "Contracts",
  MESSENGER = "Messenger",
  NOTIFICATIONS = "Notifications",
  PARAMS = "Params",
  ERROR_404 = "Error404",
  ERROR = "Error",
  INFO = "Info",
  SCANREPORT = "Scanreport",
  // testing
  TEST_CHART = "TestChart",
}

const footerMenu = [
  {
    location: "/terms",
    value: "Terms & conditions",
    onlyAdmin: false,
    small: true,
  },
  {
    location: "/privacyPolicy",
    value: "Privacy policy",
    onlyAdmin: false,
    small: true,
  },
  {
    location: "/cookie",
    value: "Cookie policy",
    onlyAdmin: false,
    small: true,
  },
  {
    location: "/refundPolicy",
    value: "Refund policy",
    onlyAdmin: false,
    small: true,
  },
  {
    location: "/acceptableUsePolicy",
    value: "Acceptable use policy",
    onlyAdmin: false,
    small: true,
  },
  {
    location: "/disclaimer",
    value: "Disclaimer",
    onlyAdmin: false,
    small: true,
  },
  {
    location: "/contacts",
    id: "menu.contacts",
    onlyAdmin: false,
    small: true,
  },
];

const menu = [
  {
    location: ROUTES_E.MACHINES,
    id: "menu.machines",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.CLIENTS,
    id: "menu.clients",
    onlyAdmin: true,
  },
  {
    location: ROUTES_E.LOCATIONS,
    id: "menu.locations_login",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.SETTINGS,
    id: "menu.settings",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.MANAGMENT,
    id: "menu.managment",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.MESSENGER,
    id: "menu.messenger",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.NOTIFICATIONS,
    id: "menu.notifications",
    onlyAdmin: false,
  },
  {},
  ...footerMenu,
  {},
];

function App() {
  const [locale, setLocale] = useState<LocaleLangE>(getLangFromLocal());
  // const auth = useSelector((state: RootState) => state.auth);
  // const dispatch = useDispatch() as RootDispatch;
  const auth = useAuth();
  const dispatch = useDispatch<RootDispatch>();
  const interval = useRef<ReturnType<typeof setInterval>>();
  const contractsUpdater = useAllContractsUpdater();
  const profilesUpdater = useAllMinerProfiles();

  useEffect(() => {
    http.utils.getTime().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (auth.state === AUTHSTATES.AUTHORIZED) {
      if (!isTokenAlive()) dispatch(logout("App"));// чтобы поскорее проверить токен
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = undefined;
      }
      interval.current = setInterval(() => {
        if (!isTokenAlive()) dispatch(logout("App"));
      }, 10 * 1000);
      contractsUpdater(); // т.к. контракты могут быть в модалке в локациях, из Machines можно наверное даже убрать этот хук
      profilesUpdater();
    } else if (interval.current) {
      clearInterval(interval.current);
      interval.current = undefined;
    }
  }, [auth.state]);

  useCurrentUser();

  //save locale
  useEffect(() => {
    saveLangToLocal(locale);
    moment.locale(locale.toLocaleLowerCase());
    // dayjs.locale(locale === LocaleLangE.RU ? "ru-ru" : "en-en");
  }, [locale]);

  return (
    <div className={styles.app}>
      <LazyMotion features={domAnimation}>
        <LocaleContext.Provider value={{ locale, setLocale }}>
          <ConfigProvider
            locale={locale === LocaleLangE.RU ? ruRU : enUS}
            button={{ className: button.button }}
            checkbox={{ className: checkbox.checkbox }}
            input={{ className: input.input }}
            select={{ className: select.select }}
            switch={{ className: switchStyle.switch }}
            tabs={{ className: tabs.tabs }}
            transfer={{ className: transfer.transfer }}
            badge={{ className: badge.badge }}
            radio={{ className: radio.radio }}
            timePicker={{ className: timePicker.timePicker }}
          >
            <SocketState>
              <BrowserRouter>
                <ProviderIntl locale={locale}>
                  <GlobalIntl />
                  <ToastContainer toastClassName={"toasts__global__class"} />
                  {auth.state === AUTHSTATES.AUTHORIZED && (
                    <Menu isAdmin={auth.isAdmin} items={menu} />
                  )}
                  <div
                    className={classNames(styles.main, {
                      [styles.auth]: auth.state === AUTHSTATES.AUTHORIZED,
                    })}
                  >
                    <Header menu={menu} />
                    <div
                      className={classNames(styles.content, {
                        [styles.auth]: auth.state === AUTHSTATES.AUTHORIZED,
                      })}
                    >
                      <ScrollableArea disableHorizontal>
                        <Routes>
                          <Route
                            path={ROUTES_E.HOME}
                            element={SuspendComponent(ComponentsE.LOGIN)}
                          />
                          <Route
                            path={ROUTES_E.LOGIN}
                            element={SuspendComponent(ComponentsE.LOGIN)}
                          />
                          <Route
                            path={ROUTES_E.ACCOUNT}
                            element={SuspendComponent(ComponentsE.USER)}
                          />
                          <Route
                            path={ROUTES_E.MACHINES}
                            element={SuspendComponent(ComponentsE.MACHINES)}
                          />
                          {IS_DEBUG && auth.isAdmin && (
                            <Route
                              path={ROUTES_E.SPAREPARTS}
                              element={SuspendComponent(ComponentsE.SPAREPARTS)}
                            />
                          )}
                          <Route
                            path={ROUTES_E.CLIENTS}
                            element={SuspendComponent(ComponentsE.CLIENTS)}
                          />
                          <Route
                            path={ROUTES_E.SETTINGS}
                            element={SuspendComponent(ComponentsE.SETTINGS)}
                          />
                          <Route
                            path={ROUTES_E.LOCATIONS}
                            element={SuspendComponent(ComponentsE.LOCATIONS)}
                          />
                          <Route
                            path={ROUTES_E.MANAGMENT}
                            element={SuspendComponent(ComponentsE.CONTRACTS)}
                          />
                          <Route
                            path={ROUTES_E.MESSENGER}
                            element={SuspendComponent(ComponentsE.MESSENGER)}
                          />
                          <Route
                            path={ROUTES_E.NOTIFICATIONS}
                            element={SuspendComponent(ComponentsE.NOTIFICATIONS)}
                          />
                          <Route
                            path={ROUTES_E.PARAMS}
                            element={SuspendComponent(ComponentsE.PARAMS)}
                          />
                          {/* Если произошла какая-то глобальная ошибка ( например ошибка верификации) - перенаправляем на эту страницу */}
                          <Route
                            path={ROUTES_E.ERROR}
                            element={SuspendComponent(ComponentsE.ERROR)}
                          />
                          <Route
                            path={ROUTES_E.INFO}
                            element={SuspendComponent(ComponentsE.INFO)}
                          />
                          <Route
                            path={ROUTES_E.SCANREPORT}
                            element={SuspendComponent(ComponentsE.SCANREPORT)}
                          />
                          {IS_DEBUG && (
                            <Route
                              path={ROUTES_E.TEST_CHART}
                              element={SuspendComponent(ComponentsE.TEST_CHART)}
                            />
                          )}
                          {/* все что не подходит под роутинг - перенаправляется на главную */}
                          <Route
                            path={"*"}
                            element={SuspendComponent(ComponentsE.ERROR_404)}
                          />

                          {/* <Route path='/success_contacts' element={<Success />} />
                          <Route
                            path='/disclaimer'
                            element={
                              <MDPages
                                md={locale === LocaleLangE.EN ? MDE.D_ENG : MDE.D_RU}
                              />
                            }
                          /> */}
                          {/* <Route
                            path='/cookie'
                            element={
                              <MDPages
                                md={locale === LocaleLangE.EN ? MDE.C_ENG : MDE.C_RU}
                              />
                            }
                          />
                          <Route
                            path='/terms'
                            element={
                              <MDPages
                                md={locale === LocaleLangE.EN ? MDE.T_ENG : MDE.T_RU}
                              />
                            }
                          />
                          <Route
                            path='/privacyPolicy'
                            element={
                              <MDPages
                                md={
                                  locale === LocaleLangE.EN ? MDE.PP_ENG : MDE.PP_RU
                                }
                              />
                            }
                          /> */}
                        </Routes>
                      </ScrollableArea>
                    </div>
                    {auth.state === AUTHSTATES.UNAUTHORIZED && (
                      <Footer menu={footerMenu} />
                    )}
                  </div>
                </ProviderIntl>
              </BrowserRouter>
            </SocketState>
          </ConfigProvider>
        </LocaleContext.Provider>
      </LazyMotion>
    </div>
  );
}

export default App;
